import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../../../lib/api";
import { formatPrice } from "../../../lib/formatPrice";
import LabBookingLayout from "../components/LabBookingLayout";
import { useLabBooking } from "../context/LabBookingContext";

// Matches App\Http\Constants.php's RELATIONSHIP array (excludes SELF, which
// is auto-created at signup and always returned first by POST /user/patient).
const RELATION_OPTIONS = [
  "GRAND FATHER",
  "GRAND MOTHER",
  "FATHER",
  "MOTHER",
  "WIFE",
  "HUSBAND",
  "SISTER",
  "BROTHER",
  "SON",
  "DAUGHTER",
  "COUSIN",
  "AUNT",
  "UNCLE",
  "GUEST",
  "OTHER",
];

const PatientDetailsPage = () => {
  const navigate = useNavigate();
  const { user, setPatient, selectedLab, collectionType } = useLabBooking();
  const [patients, setPatients] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    mobile: "",
    dob: "",
    gender: "Male",
    relation: RELATION_OPTIONS[0],
  });

  useEffect(() => {
    if (!user) return;
    setLoading(true);
    api
      .post("/user/patient")
      .then((res) => {
        const list = res.data?.data || [];
        setPatients(list);
        const self = list.find((p) => p.relation === "SELF");
        setSelectedId((self || list[0])?.id ?? null);
      })
      .catch(() => setError("Couldn't load patients."))
      .finally(() => setLoading(false));
  }, [user]);

  const update = (field) => (e) =>
    setForm((prev) => ({ ...prev, [field]: e.target.value }));

  const handleAddPatient = () => {
    if (!form.first_name.trim() || !form.last_name.trim() || form.mobile.trim().length !== 10) {
      setError("Name and a 10-digit mobile number are required.");
      return;
    }
    setSaving(true);
    setError(null);
    api
      .post("/user/patient/add", form)
      .then((res) => {
        if (res.data?.code === 200) {
          const created = res.data.data;
          setPatients((prev) => [...prev, created]);
          setSelectedId(created.id);
          setShowForm(false);
          setForm({
            first_name: "",
            last_name: "",
            mobile: "",
            dob: "",
            gender: "Male",
            relation: RELATION_OPTIONS[0],
          });
        } else {
          setError(res.data?.message || "Couldn't save patient.");
        }
      })
      .catch(() => setError("Couldn't save patient."))
      .finally(() => setSaving(false));
  };

  const handleContinue = () => {
    const patient = patients.find((p) => p.id === selectedId);
    if (!patient) return;
    setPatient(patient);
    navigate("/lab-booking/review");
  };

  const lineItems = selectedLab?.testList || [];
  const isHome    = collectionType !== "lab";
  const subtotal  = lineItems.reduce((sum, i) => sum + (Number(i.price) - (Number(i.price) * Number(i.discount || 0)) / 100), 0);
  const mrpTotal  = lineItems.reduce((sum, i) => sum + Number(i.price), 0);

  if (!user) {
    return (
      <LabBookingLayout>
        <p className="text-[13px] text-[#6B7A99]">
          Please{" "}
          <button
            onClick={() => navigate("/lab-booking/login")}
            className="text-[#13B58E] font-semibold underline"
          >
            sign in
          </button>{" "}
          to continue.
        </p>
      </LabBookingLayout>
    );
  }

  return (
    <LabBookingLayout>
      <div className="grid grid-cols-12 gap-6">
        <div className="col-span-12 lg:col-span-8 bg-white rounded-2xl border border-gray-100 p-6">
          <h1 className="text-[18px] font-extrabold text-[#191C1D] mb-1">
            Patient Details
          </h1>
          <p className="text-[12px] text-[#6B7A99] mb-4">Who is this test for?</p>

          {loading && (
            <p className="text-[12px] text-[#6B7A99] mb-3">Loading patients...</p>
          )}

          <div className="flex flex-wrap gap-2 mb-5">
            {patients.map((p) => (
              <button
                key={p.id}
                onClick={() => setSelectedId(p.id)}
                className={`text-[12px] font-semibold px-4 py-2 rounded-full transition ${
                  selectedId === p.id
                    ? "bg-[#13B58E] text-white"
                    : "border border-gray-200 text-[#3D4945]"
                }`}
              >
                {p.relation === "SELF"
                  ? `Self (${p.first_name || "You"})`
                  : `${p.first_name} ${p.last_name} (${p.relation})`}
              </button>
            ))}
            <button
              onClick={() => setShowForm((v) => !v)}
              className="flex items-center gap-1.5 text-[12px] font-semibold px-4 py-2 rounded-full border border-dashed border-[#13B58E] text-[#13B58E]"
            >
              + Add Patient
            </button>
          </div>

          {error && <p className="text-[12px] text-red-500 mb-4">{error}</p>}

          {showForm && (
            <div className="border-t border-gray-100 pt-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="text-[12px] font-semibold text-[#3D4945]">
                  First Name
                </label>
                <input
                  value={form.first_name}
                  onChange={update("first_name")}
                  className="w-full h-11 mt-1.5 px-3.5 rounded-lg border border-gray-200 text-[14px] focus:outline-none focus:ring-1 focus:ring-[#13B58E]"
                />
              </div>
              <div>
                <label className="text-[12px] font-semibold text-[#3D4945]">
                  Last Name
                </label>
                <input
                  value={form.last_name}
                  onChange={update("last_name")}
                  className="w-full h-11 mt-1.5 px-3.5 rounded-lg border border-gray-200 text-[14px] focus:outline-none focus:ring-1 focus:ring-[#13B58E]"
                />
              </div>
              <div>
                <label className="text-[12px] font-semibold text-[#3D4945]">
                  Mobile Number
                </label>
                <input
                  value={form.mobile}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      mobile: e.target.value.replace(/\D/g, ""),
                    }))
                  }
                  maxLength={10}
                  className="w-full h-11 mt-1.5 px-3.5 rounded-lg border border-gray-200 text-[14px] focus:outline-none focus:ring-1 focus:ring-[#13B58E]"
                />
              </div>
              <div>
                <label className="text-[12px] font-semibold text-[#3D4945]">
                  Date of Birth
                </label>
                <input
                  type="date"
                  value={form.dob}
                  onChange={update("dob")}
                  className="w-full h-11 mt-1.5 px-3.5 rounded-lg border border-gray-200 text-[14px] focus:outline-none focus:ring-1 focus:ring-[#13B58E]"
                />
              </div>
              <div>
                <label className="text-[12px] font-semibold text-[#3D4945]">
                  Gender
                </label>
                <select
                  value={form.gender}
                  onChange={update("gender")}
                  className="w-full h-11 mt-1.5 px-3.5 rounded-lg border border-gray-200 text-[14px] focus:outline-none focus:ring-1 focus:ring-[#13B58E]"
                >
                  <option>Male</option>
                  <option>Female</option>
                  <option>Unisex</option>
                </select>
              </div>
              <div>
                <label className="text-[12px] font-semibold text-[#3D4945]">
                  Relation
                </label>
                <select
                  value={form.relation}
                  onChange={update("relation")}
                  className="w-full h-11 mt-1.5 px-3.5 rounded-lg border border-gray-200 text-[14px] focus:outline-none focus:ring-1 focus:ring-[#13B58E]"
                >
                  {RELATION_OPTIONS.map((r) => (
                    <option key={r}>{r}</option>
                  ))}
                </select>
              </div>
              <div className="sm:col-span-2">
                <button
                  onClick={handleAddPatient}
                  disabled={saving}
                  className="bg-[#13B58E] text-white text-[13px] font-semibold px-5 py-2.5 rounded-full hover:bg-[#0fa07d] transition disabled:opacity-60"
                >
                  {saving ? "Saving..." : "Save Patient"}
                </button>
              </div>
            </div>
          )}

          <div className="flex items-start gap-2 bg-[#EEF3FB] border border-[#C9DCF5] rounded-xl p-3.5 text-[12px] text-[#3D5A8A] mt-5">
            Fasting Required for some tests: Please fast for 10-12 hours
            before sample collection. Water intake is allowed.
          </div>

          <div className="flex gap-3 mt-6">
            <button
              onClick={() => navigate(-1)}
              className="px-5 py-2.5 rounded-full border border-gray-200 text-[#3D4945] text-[13px] font-semibold"
            >
              Back
            </button>
            <button
              onClick={handleContinue}
              disabled={!selectedId}
              className="flex-1 bg-[#13B58E] text-white text-[14px] font-semibold py-3 rounded-full hover:bg-[#0fa07d] transition disabled:opacity-60"
            >
              Save & Continue to Address →
            </button>
          </div>
        </div>

        <div className="col-span-12 lg:col-span-4">
          <div className="bg-white rounded-2xl border border-gray-100 p-5 sticky top-24">
            <h3 className="text-[14px] font-bold text-[#191C1D] mb-3">
              Order Summary
            </h3>
            <div className="space-y-2 text-[13px]">
              {lineItems.map((item) => {
                const mrp        = Number(item.price);
                const finalPrice = mrp - (mrp * Number(item.discount || 0)) / 100;
                const hasDiscount = Number(item.discount) > 0;
                return (
                  <div key={item.id}>
                    <span className="text-[#3D4945]">{item.name}</span>
                    <div className="flex items-baseline gap-1.5 mt-0.5">
                      {hasDiscount && (
                        <span className="text-[11px] text-[#9AA6A4] line-through">₹{formatPrice(mrp)}</span>
                      )}
                      <span className="text-[#191C1D] font-semibold">₹{formatPrice(finalPrice)}</span>
                      {hasDiscount && (
                        <span className="text-[10px] text-[#13B58E] font-semibold">
                          ({Math.round(Number(item.discount))}% off)
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
              {isHome && (
                <div className="flex justify-between text-[12px] text-[#9AA6A4] pt-1">
                  <span>Home Collection Fee</span>
                  <span>Added next step</span>
                </div>
              )}
            </div>
            <div className="border-t border-gray-100 mt-3 pt-3">
              <div className="flex justify-between items-baseline">
                <span className="text-[14px] font-bold text-[#191C1D]">
                  {isHome ? "Test Subtotal" : "Total Amount"}
                </span>
                <div className="text-right">
                  {mrpTotal > subtotal && (
                    <p className="text-[11px] text-[#9AA6A4] line-through">₹{formatPrice(mrpTotal)}</p>
                  )}
                  <span className="text-[16px] font-extrabold text-[#191C1D]">₹{formatPrice(subtotal)}</span>
                </div>
              </div>
              {isHome && (
                <p className="text-[11px] text-[#9AA6A4] mt-1">
                  Final amount confirmed on Review Order screen
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    </LabBookingLayout>
  );
};

export default PatientDetailsPage;
